/**
 * Audio Visualizer for Quran Radio
 * Creates retro-style audio bars visualization using Web Audio API
 */

export class AudioVisualizer {
  private canvas: HTMLCanvasElement
  private ctx: CanvasRenderingContext2D
  private audioContext: AudioContext | null = null
  private analyser: AnalyserNode | null = null
  private source: MediaElementAudioSourceNode | null = null
  private dataArray: Uint8Array | null = null
  private animationId: number | null = null
  private _isConnected = false

  // Visual settings
  private barCount = 32
  private barGap = 2
  private barMinHeight = 2

  // Colors (matching retro neon theme)
  private colors = {
    cyan: '#00ffff',
    magenta: '#ff00ff',
    yellow: '#ffff00',
    green: '#00ff88',
  }

  constructor(canvasId: string) {
    const canvas = document.getElementById(canvasId) as HTMLCanvasElement | null
    if (!canvas) {
      throw new Error(`Canvas element with id "${canvasId}" not found`)
    }
    this.canvas = canvas
    this.ctx = canvas.getContext('2d')!

    // Handle resize
    window.addEventListener('resize', () => this.handleResize())
    this.handleResize()
  }

  get isConnected(): boolean {
    return this._isConnected
  }

  private handleResize(): void {
    // Get the parent container size
    const parent = this.canvas.parentElement
    const rect = parent?.getBoundingClientRect()

    // Set canvas size
    this.canvas.width = (rect?.width || 300) - 16 // Account for padding
    this.canvas.height = 80

    // Redraw if not animating
    if (!this.animationId) {
      this.drawIdle()
    }
  }

  /**
   * Connect to audio element
   */
  connect(audioElement: HTMLAudioElement): void {
    if (this._isConnected) {
      console.log('[Visualizer] Already connected')
      return
    }

    try {
      // Create audio context
      this.audioContext = new (window.AudioContext || (window as any).webkitAudioContext)()

      // Create analyser node
      this.analyser = this.audioContext.createAnalyser()
      this.analyser.fftSize = 128
      this.analyser.smoothingTimeConstant = 0.8

      // Connect source
      this.source = this.audioContext.createMediaElementSource(audioElement)
      this.source.connect(this.analyser)
      this.analyser.connect(this.audioContext.destination)

      // Create data array for frequency data
      this.dataArray = new Uint8Array(this.analyser.frequencyBinCount)

      this._isConnected = true
      console.log('[Visualizer] Connected to audio element')
    } catch (error) {
      console.error('[Visualizer] Connection error:', error)
      // Fallback: draw idle visualization
      this.drawIdle()
    }
  }

  /**
   * Start visualization animation
   */
  start(): void {
    if (this.animationId) {
      return // Already running
    }

    // Resume audio context if suspended (browser autoplay policy)
    if (this.audioContext && this.audioContext.state === 'suspended') {
      this.audioContext.resume()
    }

    this.animate()
    console.log('[Visualizer] Started')
  }

  /**
   * Stop visualization animation
   */
  stop(): void {
    if (this.animationId) {
      cancelAnimationFrame(this.animationId)
      this.animationId = null
    }
    this.drawIdle()
    console.log('[Visualizer] Stopped')
  }

  /**
   * Animation loop
   */
  private animate(): void {
    this.animationId = requestAnimationFrame(() => this.animate())

    if (this.analyser && this.dataArray) {
      // @ts-expect-error TypeScript ES2020 TypedArray compatibility
      this.analyser.getByteFrequencyData(this.dataArray)
      this.draw()
    } else {
      this.drawIdle()
    }
  }

  /**
   * Draw visualization bars
   */
  private draw(): void {
    const { ctx, canvas, dataArray, barCount, barGap, barMinHeight } = this
    const width = canvas.width
    const height = canvas.height

    // Clear canvas
    ctx.clearRect(0, 0, width, height)

    if (!dataArray) return

    // Calculate bar dimensions
    const totalGap = (barCount - 1) * barGap
    const barWidth = (width - totalGap) / barCount
    const step = Math.floor(dataArray.length / barCount)

    // Draw each bar
    for (let i = 0; i < barCount; i++) {
      // Get average frequency for this bar
      let sum = 0
      for (let j = 0; j < step; j++) {
        sum += dataArray[i * step + j]
      }
      const average = sum / step

      // Calculate bar height (0-255 -> 0-height)
      const barHeight = Math.max(barMinHeight, (average / 255) * height * 0.9)

      // Calculate position
      const x = i * (barWidth + barGap)
      const y = height - barHeight

      // Get color based on position
      const color = this.getBarColor(i, barCount, average)

      // Draw bar with glow effect
      ctx.shadowColor = color
      ctx.shadowBlur = 10
      ctx.fillStyle = color

      // Draw rounded bar
      this.drawRoundedRect(x, y, barWidth, barHeight, 2)
    }

    // Reset shadow
    ctx.shadowBlur = 0
  }

  /**
   * Draw idle state visualization (subtle animation)
   */
  private drawIdle(): void {
    const { ctx, canvas, barCount, barGap, barMinHeight } = this
    const width = canvas.width
    const height = canvas.height

    // Clear canvas
    ctx.clearRect(0, 0, width, height)

    // Calculate bar dimensions
    const totalGap = (barCount - 1) * barGap
    const barWidth = (width - totalGap) / barCount

    // Get current time for subtle animation
    const time = Date.now() / 1000

    // Draw each bar with subtle idle animation
    for (let i = 0; i < barCount; i++) {
      // Subtle wave effect
      const wave = Math.sin(time * 2 + i * 0.3) * 0.5 + 0.5
      const barHeight = Math.max(barMinHeight, wave * 8 + 2)

      const x = i * (barWidth + barGap)
      const y = height - barHeight

      // Dimmed color for idle state
      const color = 'rgba(0, 255, 255, 0.3)'

      ctx.shadowColor = 'rgba(0, 255, 255, 0.5)'
      ctx.shadowBlur = 5
      ctx.fillStyle = color

      this.drawRoundedRect(x, y, barWidth, barHeight, 2)
    }

    ctx.shadowBlur = 0

    // Continue idle animation
    if (!this.animationId) {
      requestAnimationFrame(() => {
        if (!this.animationId) {
          this.drawIdle()
        }
      })
    }
  }

  /**
   * Get bar color based on position and intensity
   */
  private getBarColor(index: number, total: number, _intensity: number): string {
    // Create gradient effect across bars
    const position = index / total

    // Color transitions based on position
    if (position < 0.33) {
      // Cyan to green
      return this.interpolateColor(this.colors.cyan, this.colors.green, position * 3)
    } else if (position < 0.66) {
      // Green to yellow
      return this.interpolateColor(this.colors.green, this.colors.yellow, (position - 0.33) * 3)
    } else {
      // Yellow to magenta
      return this.interpolateColor(this.colors.yellow, this.colors.magenta, (position - 0.66) * 3)
    }
  }

  /**
   * Interpolate between two hex colors
   */
  private interpolateColor(color1: string, color2: string, factor: number): string {
    const r1 = parseInt(color1.slice(1, 3), 16)
    const g1 = parseInt(color1.slice(3, 5), 16)
    const b1 = parseInt(color1.slice(5, 7), 16)

    const r2 = parseInt(color2.slice(1, 3), 16)
    const g2 = parseInt(color2.slice(3, 5), 16)
    const b2 = parseInt(color2.slice(5, 7), 16)

    const r = Math.round(r1 + (r2 - r1) * factor)
    const g = Math.round(g1 + (g2 - g1) * factor)
    const b = Math.round(b1 + (b2 - b1) * factor)

    return `rgb(${r}, ${g}, ${b})`
  }

  /**
   * Draw rounded rectangle
   */
  private drawRoundedRect(x: number, y: number, width: number, height: number, radius: number): void {
    const { ctx } = this

    ctx.beginPath()
    ctx.moveTo(x + radius, y)
    ctx.lineTo(x + width - radius, y)
    ctx.quadraticCurveTo(x + width, y, x + width, y + radius)
    ctx.lineTo(x + width, y + height)
    ctx.lineTo(x, y + height)
    ctx.lineTo(x, y + radius)
    ctx.quadraticCurveTo(x, y, x + radius, y)
    ctx.closePath()
    ctx.fill()
  }

  /**
   * Cleanup resources
   */
  destroy(): void {
    this.stop()

    if (this.source) {
      this.source.disconnect()
      this.source = null
    }

    if (this.analyser) {
      this.analyser.disconnect()
      this.analyser = null
    }

    if (this.audioContext) {
      this.audioContext.close()
      this.audioContext = null
    }

    this._isConnected = false
  }
}